import React, { useContext, useState, useRef, useMemo, useEffect } from 'react';
import { Box, Stack, styled, Button, Popover, CircularProgress as MuiCircularProgress, Select, MenuItem, FormControl, InputLabel, ToggleButtonGroup, ToggleButton, Typography } from '@mui/material';
import SubmitWithTimeButton from '../SubmitWithTimeButton/SubmitWithTimeButton';
import { AuthContext, MyContext } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { MentionsInput, Mention } from 'react-mentions';
import { CacheContext, CacheContextType } from '../context/CacheContext';
import AvatarIcon from '../resource/AvatarIcon';
import ImageIcon from '../resource/ImageIcon';
import { Tooltip, IconButton, TextField, Collapse, Chip } from '@mui/material';
import { supabase } from '../supabaseClient';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PollIcon from '@mui/icons-material/Poll';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { BURIAL_PRESETS, MAX_BURIAL_DAYS, formatOpenAt, formatTimeUntilOpen } from '../lib/timeCapsule';
import { DateTime } from 'luxon';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import CasinoIcon from '@mui/icons-material/Casino';
import LinkPreviewCard, { extractPreviewUrl } from '../components/LinkPreviewCard';
import SparkButton from '../components/SparkButton';

// Creative prompts for the "stuck?" dice button. Picked at random
// each click — the chosen prompt is inserted as the current draft.
const STUCK_PROMPTS = [
  'what do you wish you could remember about today?',
  'the strangest thing you saw this week: ',
  'a thought that will embarrass future-you in 24h: ',
  'if this post were a postcard, it would say: ',
  'a piece of advice i\'d tell my past self: ',
  'hot take, alive for 24h only: ',
  'the thing i keep thinking about today: ',
  'something i\'ve never told anyone: ',
  'a confession that dies at midnight: ',
  'the tiniest joy i had today: ',
  'a memory from exactly one year ago: ',
  'a dream i had last night: ',
  'what the silence after a song sounds like: ',
  'something i almost said today but didn\'t: ',
  'the smell of right now: ',
];

const POST_PLACEHOLDERS = [
  "What's on your mind? It won't last forever…",
  "Say something worth 24 hours of your time.",
  "Write something you'd want someone to keep alive.",
  "Leave a mark. Use @ to mention, # for feeds.",
  "What do you want the world to remember today?",
  "Time is ticking. What matters right now?",
  "Post something. It expires. That's the point.",
  "Every word costs time. Make it count.",
];

const UsernameLink = styled(Link)`
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
  color: inherit;
`;

function SubmitPostComponent({
  parentID,
  onSubmit,
  objectType,
  parentType,
  defaultComposition,
}: {
  parentID?: string;
  onSubmit?: () => void;
  objectType: string;
  parentType?: string;
  defaultComposition?: string;
}) {
  const DRAFT_KEY = 'wipe_draft';
  const [draftRestored, setDraftRestored] = useState(false);
  const draftSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getInitialComposition = () => {
    if (defaultComposition) return defaultComposition;
    if (!parentID) {
      try {
        const saved = localStorage.getItem(DRAFT_KEY);
        if (saved) { setDraftRestored(true); return saved; }
      } catch { /* ignore */ }
    }
    return '';
  };

  const [currentComposition, setCurrentComposition] = useState(getInitialComposition);

  // Listen for `wipe-composer-prefill` events fired by onboarding /
  // template features. Only top-level composers (no parentID) respond
  // so reply composers don't get hijacked.
  useEffect(() => {
    if (parentID) return;
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ text: string; replace?: boolean }>;
      const incoming = ce.detail?.text ?? '';
      if (!incoming) return;
      setCurrentComposition((prev) => ce.detail?.replace ? incoming : (prev ? prev + '\n' + incoming : incoming));
    };
    window.addEventListener('wipe-composer-prefill', handler);
    return () => window.removeEventListener('wipe-composer-prefill', handler);
  }, [parentID]);
  const placeholder = useMemo(
    () => parentID ? 'Write a reply…' : POST_PLACEHOLDERS[Math.floor(Math.random() * POST_PLACEHOLDERS.length)],
    [parentID], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const users = useContext<CacheContextType>(CacheContext).allUsers();
  const feeds = useContext<CacheContextType>(CacheContext).allFeeds();
  const [selectedGif, setSelectedGif] = useState<string | null>(null);
  const [showMediaInput, setShowMediaInput] = useState(false);
  const [mediaUrl, setMediaUrl] = useState('');

  const [mentions, setMentions] = useState<string[]>([]);
  const [feedTags, setFeedTags] = useState<string[]>([]);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<null | HTMLElement>(null);
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  // Poll state
  const [showPoll, setShowPoll] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [pollType, setPollType] = useState<'standard' | 'survival' | 'race'>('standard');
  const [pollTimePerVote, setPollTimePerVote] = useState(3600);      // 1 hour
  const [pollRaceGoal, setPollRaceGoal] = useState(86400);           // 24 hours

  // Time capsule state
  const [buryOpen, setBuryOpen] = useState(false);           // bury panel expanded
  const [openAtISO, setOpenAtISO] = useState<string | null>(null); // null = not buried
  const [customBuryLocal, setCustomBuryLocal] = useState(''); // datetime-local input

  const handleToggleBury = () => {
    setBuryOpen((v) => {
      if (!v) {
        // Default preset: 1 week
        const preset = BURIAL_PRESETS.find((p) => p.label === '1 week') ?? BURIAL_PRESETS[1];
        setOpenAtISO(DateTime.now().plus({ days: preset.days }).toISO());
      } else {
        setOpenAtISO(null);
        setCustomBuryLocal('');
      }
      return !v;
    });
  };

  const applyBuryPreset = (days: number) => {
    setOpenAtISO(DateTime.now().plus({ days }).toISO());
    setCustomBuryLocal('');
  };

  const applyBuryCustom = (localStr: string) => {
    setCustomBuryLocal(localStr);
    if (!localStr) { setOpenAtISO(null); return; }
    const dt = DateTime.fromISO(localStr);
    if (!dt.isValid) return;
    if (dt <= DateTime.now()) return; // must be in the future
    if (dt > DateTime.now().plus({ days: MAX_BURIAL_DAYS })) return;
    setOpenAtISO(dt.toISO());
  };

  const handleTogglePoll = () => {
    setShowPoll((v) => {
      if (!v) {
        setPollQuestion('');
        setPollOptions(['', '']);
        setPollType('standard');
      }
      return !v;
    });
  };

  const handlePollTypeChange = (_: any, v: string | null) => {
    if (!v) return;
    const t = v as 'standard' | 'survival' | 'race';
    setPollType(t);
    if (t === 'survival') setPollOptions(['⏳ Keep Alive', '💀 Let It Die']);
    else if (pollOptions[0] === '⏳ Keep Alive') setPollOptions(['', '']);
  };

  const pollDraft = showPoll && pollOptions.filter(Boolean).length >= 2 ? {
    question: pollQuestion,
    pollType,
    timePerVote: pollType === 'standard' ? 0 : pollTimePerVote,
    raceGoalSeconds: pollType === 'race' ? pollRaceGoal : 0,
    options: pollType === 'survival' ? ['⏳ Keep Alive', '💀 Let It Die'] : pollOptions,
  } : undefined;

  const TIME_OPTIONS = [
    { label: '5 min', value: 300 },
    { label: '15 min', value: 900 },
    { label: '30 min', value: 1800 },
    { label: '1 hour', value: 3600 },
    { label: '2 hours', value: 7200 },
    { label: '6 hours', value: 21600 },
    { label: '12 hours', value: 43200 },
    { label: '24 hours', value: 86400 },
  ];

  const RACE_GOAL_OPTIONS = [
    { label: '30 min', value: 1800 },
    { label: '1 hour', value: 3600 },
    { label: '6 hours', value: 21600 },
    { label: '12 hours', value: 43200 },
    { label: '24 hours', value: 86400 },
    { label: '2 days', value: 172800 },
    { label: '7 days', value: 604800 },
  ];

  function mentionMatcher(query: string, text: string) {
    return text.toLowerCase().indexOf(query.toLowerCase()) === 0;
  }

  function searchUsernames(query: string, callback: (data: any) => void) {
    const matches = users.filter((userRecord) =>
      mentionMatcher(query, userRecord.username),
    );
    const displayMatches = matches.map((userRecord) => ({
      id: userRecord.id,
      display: userRecord.username,
    }));
    callback(displayMatches);
  }

  function searchFeeds(query: string, callback: (data: any) => void) {
    const matches = feeds.filter((feed) => mentionMatcher(query, feed.feed_tag));
    const displayMatches = matches.map((feed) => ({
      id: feed.id,
      display: feed.feed_tag,
    }));
    callback(displayMatches);
  }

  const regexpFeed = new RegExp(/#([A-Za-z0-9_]+)/gm);
  const handleCompositionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    newValue: string,
    newPlainTextValue: string,
    mentions: any,
  ) => {
    let feedMentions: Array<any> = [];
    let userMentions: Array<any> = [];
    setCurrentComposition(event.target.value);
    // Auto-save draft for top-level posts only
    if (!parentID) {
      if (draftSaveRef.current) clearTimeout(draftSaveRef.current);
      draftSaveRef.current = setTimeout(() => {
        try {
          if (event.target.value) localStorage.setItem(DRAFT_KEY, event.target.value);
          else localStorage.removeItem(DRAFT_KEY);
        } catch { /* ignore */ }
      }, 500);
    }
    mentions.forEach((mention: any) => {
      if (mention.display.includes('#')) {
        feedMentions.push(mention);
      } else {
        userMentions.push(mention);
      }
    });

    const matches = event.target.value.match(regexpFeed);
    matches?.forEach((match) => {
      feedMentions.push({ display: match });
    });
    setMentions(userMentions);
    setFeedTags(feedMentions);
  };

  const handleMediaAttach = () => {
    const url = mediaUrl.trim();
    if (!url) return;
    const prefix = currentComposition.length > 0 ? '\n' : '';
    setCurrentComposition((prev) => prev + prefix + url);
    setMediaUrl('');
    setShowMediaInput(false);
  };

  const handleSubmit = () => {
    setCurrentComposition('');
    setSelectedGif(null);
    setMediaUrl('');
    setShowMediaInput(false);
    setShowPoll(false);
    setPollQuestion('');
    setPollOptions(['', '']);
    setPollType('standard');
    setBuryOpen(false);
    setOpenAtISO(null);
    setCustomBuryLocal('');
    setDraftRestored(false);
    if (!parentID) { try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ } }
    onSubmit && onSubmit();
  };

  const handleEmojiSelect = (emoji: any) => {
    setCurrentComposition((prev) => prev + (emoji.native ?? emoji.id));
    setEmojiAnchorEl(null);
  };

  const handleStuck = () => {
    const pick = STUCK_PROMPTS[Math.floor(Math.random() * STUCK_PROMPTS.length)];
    setCurrentComposition(pick);
    // Focus and put caret at end.
    requestAnimationFrame(() => {
      const ta = document.querySelector<HTMLTextAreaElement>('textarea');
      if (ta) {
        ta.focus();
        ta.setSelectionRange(pick.length, pick.length);
      }
    });
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? 'jpg';
      const path = `uploads/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from('gifs').upload(path, file, { contentType: file.type });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('gifs').getPublicUrl(path);
      const prefix = currentComposition.length > 0 ? '\n' : '';
      setCurrentComposition((prev) => prev + prefix + publicUrl);
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await uploadFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault();
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!dropZoneRef.current?.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && (file.type.startsWith('image/') || file.type === 'video/mp4')) {
      await uploadFile(file);
    }
  };

  const userRecord = useContext<MyContext>(AuthContext).userRecord;

  // const useStyles = makeStyles({
  //   textarea: {
  //     resize: 'none',
  //   },
  // });

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        bgcolor: 'background.paper',
        margin: '0 auto',
        borderRadius: '3px',
        padding: 1,
        width: '100%',
        textAlign: 'left',
      }}
    >
      <Stack direction="row" spacing={1} sx={{ maxWidth: '560px' }}>
        <AvatarIcon style={{ width: 40, height: 40, flexShrink: 0, alignSelf: 'flex-start', marginTop: 4 }} />
        <Stack direction="column" style={{ width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.5 }}>
            <Box sx={{ fontSize: '15px', fontWeight: 700, color: 'text.primary' }}>
              {!userRecord ? 'loading' : (
                <UsernameLink to={'/' + userRecord.username}>{userRecord.username}</UsernameLink>
              )}
            </Box>
            <Box sx={{ color: 'text.secondary', fontSize: '13px' }}>
              @{userRecord?.username ?? ''}
            </Box>
          </Box>
          <Box
            ref={dropZoneRef}
            style={{ width: '100%', position: 'relative' }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onKeyDown={(e) => {
              // Cmd/Ctrl+Enter = quick submit with current settings
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                if (!currentComposition.trim()) return;
                window.dispatchEvent(new CustomEvent('wipe-composer-quick-submit'));
              }
            }}
          >
            {isDragOver && (
              <Box sx={{
                position: 'absolute', inset: 0, zIndex: 10, borderRadius: 2,
                border: '2px dashed', borderColor: 'primary.main',
                bgcolor: 'action.hover', display: 'flex', alignItems: 'center',
                justifyContent: 'center', pointerEvents: 'none',
              }}>
                <Box sx={{ fontFamily: '"VT323", monospace', fontSize: '1.1rem', color: 'primary.main', letterSpacing: 1 }}>
                  DROP TO UPLOAD
                </Box>
              </Box>
            )}
            <MentionsInput
              value={currentComposition}
              onChange={handleCompositionChange}
              placeholder={placeholder}
              style={{
                control: { fontSize: 15, fontWeight: 'normal', minHeight: 72 },
                '&multiLine': {
                  control: { fontFamily: 'inherit', minHeight: 72 },
                  highlighter: { padding: '10px 12px', border: '2px solid transparent' },
                  input: {
                    padding: '10px 12px',
                    border: '2px solid rgba(128,128,128,0.2)',
                    borderRadius: 8,
                    outline: 'none',
                    fontFamily: 'inherit',
                    fontSize: 15,
                    lineHeight: 1.5,
                    resize: 'none',
                    minHeight: 72,
                    transition: 'border-color 0.15s ease',
                  },
                },
                suggestions: {
                  list: {
                    backgroundColor: 'var(--mui-palette-background-paper, #fff)',
                    border: '1px solid rgba(128,128,128,0.2)',
                    borderRadius: 8,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    fontSize: 14,
                    overflow: 'hidden',
                    zIndex: 1400,
                  },
                  item: {
                    padding: '8px 12px',
                    '&focused': {
                      backgroundColor: 'rgba(25,118,210,0.08)',
                    },
                  },
                },
              }}
            >
              <Mention
                trigger={/@((..*))/}
                data={searchUsernames}
                appendSpaceOnAdd={true}
                displayTransform={(id, display) => `@${display}`}
              />
              <Mention
                trigger={/#((..*))/}
                data={searchFeeds}
                appendSpaceOnAdd={true}
                markup="#[__display__](__id__)"
                displayTransform={(id, display) => `#${display}`}
              />
            </MentionsInput>

            {/* Draft restored indicator */}
            {draftRestored && (
              <Chip
                label="Draft restored"
                size="small"
                onDelete={() => { setDraftRestored(false); setCurrentComposition(''); try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ } }}
                sx={{ mt: 0.5, mb: 0.5, fontSize: '0.72rem', height: 20 }}
              />
            )}

            {/* Link preview card while composing */}
            {(() => {
              const previewUrl = extractPreviewUrl(currentComposition);
              return previewUrl ? <LinkPreviewCard url={previewUrl} /> : null;
            })()}

            {selectedGif && (
              <Box
                sx={{
                  mt: 2,
                  mb: 2,
                  maxWidth: '100%',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={selectedGif}
                  alt="Selected GIF"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    borderRadius: '4px',
                  }}
                />
              </Box>
            )}

            <Collapse in={showMediaInput}>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Paste image, GIF, or YouTube URL…"
                  value={mediaUrl}
                  onChange={(e) => setMediaUrl(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleMediaAttach(); } }}
                  autoFocus
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleMediaAttach}
                  disabled={!mediaUrl.trim()}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Attach
                </Button>
              </Box>
              {/* Live image preview for image/GIF URLs */}
              {mediaUrl.trim() && /\.(gif|jpe?g|png|webp|svg)(\?.*)?$/i.test(mediaUrl.trim()) && (
                <Box sx={{ mb: 1, position: 'relative', display: 'inline-block' }}>
                  <Box
                    component="img"
                    src={mediaUrl.trim()}
                    alt="Preview"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    sx={{ maxHeight: 120, maxWidth: '100%', borderRadius: 1.5, display: 'block', border: '1px solid', borderColor: 'divider' }}
                  />
                </Box>
              )}
            </Collapse>

            {/* Time capsule / bury panel */}
            <Collapse in={buryOpen}>
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  borderRadius: 0,
                  border: '2px dashed',
                  borderColor: 'divider',
                  background: 'transparent',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Inventory2OutlinedIcon sx={{ color: 'text.primary', width: 18, height: 18 }} />
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: '"VT323", monospace', letterSpacing: 1.5, fontSize: '0.9rem', color: 'text.primary', fontWeight: 700 }}
                  >
                    TIME CAPSULE
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontStyle: 'italic' }}>
                    Hidden from everyone until it opens. Lifespan starts AFTER opening.
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                  {BURIAL_PRESETS.map((p) => {
                    const presetISO = DateTime.now().plus({ days: p.days }).toISO();
                    const active = !!openAtISO && !customBuryLocal &&
                      Math.abs(DateTime.fromISO(openAtISO).toMillis() - DateTime.fromISO(presetISO!).toMillis()) < 60_000;
                    return (
                      <Chip
                        key={p.label}
                        label={p.label}
                        size="small"
                        clickable
                        onClick={() => applyBuryPreset(p.days)}
                        variant={active ? 'filled' : 'outlined'}
                        sx={{
                          fontSize: '0.72rem',
                          height: 24,
                          borderColor: active ? 'text.primary' : 'divider',
                          bgcolor: active ? 'text.primary' : 'transparent',
                          color: active ? 'background.paper' : 'text.secondary',
                          '&:hover': { bgcolor: active ? 'text.primary' : 'action.hover' },
                        }}
                      />
                    );
                  })}
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    type="datetime-local"
                    size="small"
                    label="Or pick exact date"
                    InputLabelProps={{ shrink: true }}
                    value={customBuryLocal}
                    onChange={(e) => applyBuryCustom(e.target.value)}
                    inputProps={{
                      min: DateTime.now().plus({ minutes: 5 }).toISO({ includeOffset: false })?.slice(0, 16),
                      max: DateTime.now().plus({ days: MAX_BURIAL_DAYS }).toISO({ includeOffset: false })?.slice(0, 16),
                    }}
                    sx={{ flex: 1 }}
                  />
                </Box>

                {openAtISO && (
                  <Box sx={{ mt: 1, p: 1, borderRadius: 0, bgcolor: 'transparent', border: '2px dashed', borderColor: 'divider' }}>
                    <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 700, display: 'block' }}>
                      🔒 Opens {formatOpenAt(openAtISO)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      in {formatTimeUntilOpen(openAtISO)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Collapse>

            {/* Poll creation form */}
            <Collapse in={showPoll}>
              <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <Typography variant="caption" sx={{ fontFamily: '"VT323", monospace', letterSpacing: 1, fontSize: '0.9rem', color: 'text.secondary' }}>
                    POLL TYPE
                  </Typography>
                  <ToggleButtonGroup
                    value={pollType}
                    exclusive
                    onChange={handlePollTypeChange}
                    size="small"
                    sx={{ '& .MuiToggleButton-root': { py: 0.3, px: 1, fontSize: '0.72rem', textTransform: 'none' } }}
                  >
                    <ToggleButton value="standard">Standard</ToggleButton>
                    <ToggleButton value="survival">⏳ Survival</ToggleButton>
                    <ToggleButton value="race">🏁 Race</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  placeholder="Poll question (optional)"
                  value={pollQuestion}
                  onChange={(e) => setPollQuestion(e.target.value)}
                  sx={{ mb: 1 }}
                />

                {pollType === 'survival' ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
                    Voters choose: ⏳ Keep Alive or 💀 Let It Die — each vote costs time from their wallet.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1 }}>
                    {pollOptions.map((opt, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder={`Option ${i + 1}`}
                          value={opt}
                          onChange={(e) => {
                            const next = [...pollOptions];
                            next[i] = e.target.value;
                            setPollOptions(next);
                          }}
                        />
                        {pollOptions.length > 2 && (
                          <IconButton size="small" onClick={() => setPollOptions(pollOptions.filter((_, j) => j !== i))}>
                            <RemoveCircleOutlineIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                    {pollOptions.length < 4 && (
                      <Button
                        size="small"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => setPollOptions([...pollOptions, ''])}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none', fontSize: '0.78rem' }}
                      >
                        Add option
                      </Button>
                    )}
                  </Box>
                )}

                {pollType !== 'standard' && (
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.5 }}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>Cost per vote</InputLabel>
                      <Select
                        value={pollTimePerVote}
                        label="Cost per vote"
                        onChange={(e) => setPollTimePerVote(Number(e.target.value))}
                      >
                        {TIME_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {pollType === 'race' && (
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Race goal</InputLabel>
                        <Select
                          value={pollRaceGoal}
                          label="Race goal"
                          onChange={(e) => setPollRaceGoal(Number(e.target.value))}
                        >
                          {RACE_GOAL_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                )}
              </Box>
            </Collapse>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 1.5,
                gap: 1,
                // Allow the row to wrap on narrow viewports so the submit
                // button never gets pushed off-screen. On wider screens
                // everything still fits on one line naturally.
                flexWrap: 'wrap',
                rowGap: 1,
                minWidth: 0,
              }}
            >
              <Box
                className="wipe-composer-toolbar"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  // Let icon buttons wrap to a second row on narrow
                  // viewports instead of hiding behind a horizontal
                  // scroll. The toolbar is a discoverability surface —
                  // hidden icons don't get used.
                  flexWrap: 'wrap',
                  rowGap: 0.25,
                  gap: 0.25,
                  flex: '1 1 auto',
                  minWidth: 0,
                }}
              >
                <Tooltip title="Add emoji">
                  <IconButton size="small" onClick={(e) => setEmojiAnchorEl(e.currentTarget)}>
                    <EmojiEmotionsIcon sx={{ width: 20, height: 20 }} />
                  </IconButton>
                </Tooltip>
                <Popover
                  open={Boolean(emojiAnchorEl)}
                  anchorEl={emojiAnchorEl}
                  onClose={() => setEmojiAnchorEl(null)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  PaperProps={{ sx: { boxShadow: 6, borderRadius: 2 } }}
                >
                  <Picker
                    data={data}
                    onEmojiSelect={handleEmojiSelect}
                    theme="auto"
                    previewPosition="none"
                    skinTonePosition="none"
                  />
                </Popover>
                {!parentID && currentComposition.length === 0 && (
                  <Tooltip title="Stuck? Roll a prompt">
                    <IconButton size="small" onClick={handleStuck}>
                      <CasinoIcon sx={{ width: 20, height: 20 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Paste image or GIF URL">
                  <IconButton size="small" onClick={() => setShowMediaInput((v) => !v)}>
                    <ImageIcon style={{ width: 20, height: 20 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Upload image from device">
                  <IconButton
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading
                      ? <MuiCircularProgress size={18} />
                      : <AttachFileIcon sx={{ width: 20, height: 20 }} />
                    }
                  </IconButton>
                </Tooltip>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/mp4"
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
                {!parentID && (
                  <Tooltip title={showPoll ? 'Remove poll' : 'Add poll'}>
                    <IconButton
                      size="small"
                      onClick={handleTogglePoll}
                      sx={{ color: showPoll ? 'primary.main' : 'inherit' }}
                    >
                      <PollIcon sx={{ width: 20, height: 20 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {!parentID && (
                  <Tooltip title={buryOpen ? 'Don\'t bury — post normally' : 'Bury as time capsule (opens on a future date)'}>
                    <IconButton
                      size="small"
                      onClick={handleToggleBury}
                      sx={{ color: buryOpen ? 'text.primary' : 'inherit' }}
                    >
                      <Inventory2OutlinedIcon sx={{ width: 20, height: 20 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {currentComposition.length > 0 && (() => {
                  const pct = Math.min(100, (currentComposition.length / 280) * 100);
                  const over = currentComposition.length > 280;
                  const warn = currentComposition.length > 260;
                  const pathColor = over ? '#b51e1b' : warn ? '#ff9800' : '#1976d2';
                  return (
                    <Tooltip title={`${280 - currentComposition.length} characters remaining`}>
                      <Box sx={{ width: 22, height: 22, position: 'relative' }}>
                        <CircularProgressbar
                          value={pct}
                          styles={buildStyles({
                            pathColor,
                            trailColor: 'rgba(128,128,128,0.15)',
                            pathTransitionDuration: 0.3,
                          })}
                        />
                        {warn && (
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.5rem',
                              fontWeight: 700,
                              color: pathColor,
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {280 - currentComposition.length}
                          </Box>
                        )}
                      </Box>
                    </Tooltip>
                  );
                })()}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                {/* Sparks only for top-level posts — not replies, polls, or buried capsules */}
                {!parentID && !showPoll && !openAtISO && (
                  <SparkButton value={currentComposition} onFired={handleSubmit} />
                )}
                {parentID ? (
                  <SubmitWithTimeButton
                    objectType={objectType}
                    value={currentComposition}
                    parentID={parentID}
                    parentType={parentType}
                    onSubmit={handleSubmit}
                    mentions={mentions}
                    feedTags={feedTags}
                  />
                ) : (
                  <SubmitWithTimeButton
                    objectType={objectType}
                    value={currentComposition}
                    onSubmit={handleSubmit}
                    mentions={mentions}
                    feedTags={feedTags}
                    pollDraft={pollDraft}
                    openAtISO={openAtISO}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
}

export default SubmitPostComponent;
